// ─────────────────────────────────────────────
// pages/ReferralPage.js
// Neighbor referral / share page
// License: MIT (your original IP)
//
// Referral tracking (PR 1): each resident gets a short code from
// get_or_create_referral_code(). Their share link carries ?ref=<code>, which
// attribution.js captures onto whatever booking the neighbor makes. Live counts
// come from get_my_referral_stats() (counts only, no PII). No reward money is
// shown here yet — the referred neighbor's first pickup is free, and that is
// the honest, live incentive. A referrer credit is a separate change.
// ─────────────────────────────────────────────

import React, { useEffect, useState } from "react";
import { NAVY, NAVY2, BLUE, BLUE2, STEEL, BORDER, card, BRAND_NAME } from "../constants";
import { supabase } from "../supabase";
import PageShell from "../components/PageShell";

const SITE = "https://loaddashservices.com";

export default function ReferralPage() {
  const [code, setCode] = useState(null);
  const [stats, setStats] = useState({ referred: 0, completed: 0, availableCents: 0 });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: c } = await supabase.rpc("get_or_create_referral_code");
        if (alive && c) setCode(c);
      } catch (e) { /* code stays null → link falls back to the plain site */ }
      try {
        const { data: s } = await supabase.rpc("get_my_referral_stats");
        const row = Array.isArray(s) ? s[0] : s;
        if (alive && row) setStats({ referred: row.referred || 0, completed: row.completed || 0, availableCents: row.available_cents || 0 });
      } catch (e) { /* stats stay zero */ }
    })();
    return () => { alive = false; };
  }, []);

  const link = code ? `${SITE}/?ref=${code}` : SITE;
  const message = `Hey! I've been using ${BRAND_NAME} for junk pickup on base and it's super easy. Your first pickup is free right now. Book here: ${link}`;
  const creditDollars = Math.round(stats.availableCents / 100);

  function handleShare() {
    if (navigator.share) {
      navigator.share({ title: `${BRAND_NAME} — Hill AFB`, text: message, url: link }).catch(() => {});
    } else {
      navigator.clipboard.writeText(message).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
    }
  }

  function handleCopy() {
    navigator.clipboard.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  }

  const shareOptions = [
    { title: "Text a neighbor",  desc: "Send your link in a text or iMessage" },
    { title: "Facebook Groups",  desc: "Post in your base housing or Hill AFB community group" },
    { title: "Nextdoor",         desc: "Share in your Boyer Hill neighborhood on Nextdoor" },
    { title: "Word of mouth",    desc: "Someone PCSing soon? Point them to your link" },
  ];

  return (
    <PageShell>
      <div style={{ background: `linear-gradient(135deg,${NAVY},${NAVY2})`, padding: "28px 20px 24px" }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: "#42a5f5", marginBottom: 6 }}>Community</div>
        <h2 style={{ fontWeight: 900, fontSize: 26, color: "#fff", margin: "0 0 6px" }}>Invite a Neighbor</h2>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)" }}>Help your neighbors discover {BRAND_NAME}</div>
      </div>

      <div style={{ padding: "20px 18px", display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Share card */}
        <div style={{ ...card, textAlign: "center", padding: "28px 20px" }}>
          <div style={{ fontWeight: 800, fontSize: 18, color: NAVY, marginBottom: 8 }}>Spread the word on base</div>
          <div style={{ fontSize: 13, color: STEEL, lineHeight: 1.6, marginBottom: 20 }}>
            Send your link to a neighbor. Their first pickup is free, and you get $15 off your next pickup when they complete theirs.
          </div>
          <button onClick={handleShare} style={{ width: "100%", minHeight: 44, padding: "14px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${BLUE},${BLUE2})`, color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", boxShadow: "0 4px 16px rgba(21,101,192,.3)" }}>
            Share with Neighbors
          </button>
        </div>

        {/* Your link */}
        <div style={{ ...card, padding: "16px" }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: STEEL, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Your invite link</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ flex: 1, background: "#f0f4f8", borderRadius: 8, padding: "10px 12px", fontSize: 13, color: NAVY, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {code ? `loaddashservices.com/?ref=${code}` : "loaddashservices.com"}
            </div>
            <button onClick={handleCopy}
              style={{ minHeight: 44, minWidth: 44, padding: "10px 16px", borderRadius: 8, border: "none", background: NAVY, color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <div style={{ fontSize: 12, color: STEEL, marginTop: 8, lineHeight: 1.5 }}>
            Their first pickup is free. When they book through your link, it counts here.
          </div>
        </div>

        {/* Your referrals */}
        <div style={{ ...card }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: STEEL, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>Your referrals</div>
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1, background: "#f0f4f8", borderRadius: 10, padding: "14px 12px", textAlign: "center" }}>
              <div style={{ fontWeight: 900, fontSize: 26, color: NAVY }}>{stats.referred}</div>
              <div style={{ fontSize: 11, color: STEEL, fontWeight: 600, marginTop: 2 }}>Booked</div>
            </div>
            <div style={{ flex: 1, background: "#f0f4f8", borderRadius: 10, padding: "14px 12px", textAlign: "center" }}>
              <div style={{ fontWeight: 900, fontSize: 26, color: "#03A66A" }}>{stats.completed}</div>
              <div style={{ fontSize: 11, color: STEEL, fontWeight: 600, marginTop: 2 }}>Completed</div>
            </div>
          </div>
          {creditDollars > 0 ? (
            <div style={{ marginTop: 12, background: "rgba(3,166,106,.10)", border: "1px solid rgba(3,166,106,.35)", borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
              <div style={{ fontWeight: 800, fontSize: 20, color: "#03A66A" }}>${creditDollars} off your next pickup</div>
              <div style={{ fontSize: 12, color: STEEL, marginTop: 3 }}>Applied automatically at checkout.</div>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: STEEL, lineHeight: 1.5, marginTop: 12, textAlign: "center" }}>
              You earn $15 off your next pickup each time a neighbor you referred completes their first pickup.
            </div>
          )}
        </div>

        {/* How to share */}
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 14 }}>Easy ways to share</div>
          {shareOptions.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 0", borderBottom: i < shareOptions.length - 1 ? `1px solid ${BORDER}` : "none" }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#f0f4f8", fontSize: 10, fontWeight: 700, color: "#6b7f96", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 2 }}>{s.title}</div>
                <div style={{ fontSize: 12, color: STEEL, lineHeight: 1.5 }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
